import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SEOHead from './SEOHead';
import Chart from 'chart.js/auto';
import moment from 'moment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import TrendingUp from '@mui/icons-material/TrendingUp';
import Savings from '@mui/icons-material/Savings';
import ShowChart from '@mui/icons-material/ShowChart';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import FlightTakeoff from '@mui/icons-material/FlightTakeoff';
import Share from '@mui/icons-material/Share';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { useLanguage } from '../context/LanguageContext';
import { useT } from '../translations';

const BOX3_START_YEAR = 2028;
const BOX3_TAX_RATE = 0.36;
const BOX3_FREE_RETURN = 1_800;

const computeYearlyData = ({ years, principal, monthlyContribution, interestRate, box3Enabled }) => {
	const currentYear = parseInt(moment().format('YYYY'));
	const monthlyRate = interestRate / 12;
	const annualFactor = Math.pow(1 + monthlyRate, 12);
	const annualContribFactor = monthlyRate > 0 ? (annualFactor - 1) / monthlyRate : 12;

	const compounds = [];
	const invested = [];
	const box3Values = [];

	let wealth = principal;
	let box3Wealth = principal;

	for (let t = 1; t <= years; t++) {
		const calendarYear = currentYear + t;
		const box3YearStart = box3Wealth;

		wealth = wealth * annualFactor + monthlyContribution * annualContribFactor;
		box3Wealth = box3YearStart * annualFactor + monthlyContribution * annualContribFactor;

		if (box3Enabled && calendarYear >= BOX3_START_YEAR) {
			const grossReturn = box3YearStart * (annualFactor - 1);
			const taxableReturn = Math.max(0, grossReturn - BOX3_FREE_RETURN);
			const tax = taxableReturn * BOX3_TAX_RATE;
			box3Wealth = Math.max(0, box3Wealth - tax);
		}

		compounds.push(parseFloat(wealth.toFixed(2)));
		box3Values.push(parseFloat(box3Wealth.toFixed(2)));
		invested.push(principal + monthlyContribution * 12 * t);
	}

	return { compounds, invested, box3Values };
};

const getYearLabels = (amountYears) => {
	const startYear = parseInt(moment().format('YYYY'));
	return Array.from({ length: amountYears }, (_, i) => (startYear + i + 1).toString());
};

const formatEuro = (value) =>
	value?.toLocaleString('nl-NL', {
		style: 'currency',
		currency: 'EUR',
		maximumFractionDigits: 0,
	});

const StatCard = ({ label, value, highlight, icon: Icon, subValue, subLabel, delay = 0 }) => (
	<Paper
		elevation={0}
		className={highlight ? 'stat-card-highlight' : 'animate-fade-up'}
		style={!highlight ? { '--delay': `${delay}ms` } : undefined}
		sx={{
			p: 2.5,
			borderRadius: 3,
			bgcolor: highlight ? 'primary.main' : 'grey.100',
			color: highlight ? 'white' : 'text.primary',
			textAlign: 'center',
			height: '100%',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			gap: 0.5,
			transition: 'transform 0.25s ease, box-shadow 0.25s ease',
			'&:hover': { transform: 'translateY(-3px)' },
		}}
	>
		{Icon && <Icon sx={{ fontSize: 28, opacity: highlight ? 0.9 : 0.5, mb: 0.5 }} />}
		<Typography variant="body2" sx={{ opacity: 0.75, fontWeight: 500 }}>
			{label}
		</Typography>
		<Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
			{value}
		</Typography>
		{subValue && (
			<Typography
				variant="caption"
				sx={{ color: highlight ? 'rgba(255,200,100,1)' : 'warning.dark', fontWeight: 500 }}
			>
				{subLabel}: {subValue}
			</Typography>
		)}
	</Paper>
);

const readUrlParams = () => {
	const p = new URLSearchParams(window.location.search);
	return {
		years: p.get('jaren') || '30',
		principal: p.get('start') || '0',
		monthlyContribution: p.get('inleg') || '250',
		annualInterestRate: p.get('rendement') || '6',
	};
};

const Graph = ({ onEmigrate }) => {
	const navigate = useNavigate();
	const { language } = useLanguage();
	const t = useT(language);
	const [copied, setCopied] = useState(false);
	const initial = useMemo(readUrlParams, []);
	const [years, setYears] = useState(initial.years);
	const [principal, setPrincipal] = useState(initial.principal);
	const [monthlyContribution, setMonthlyContribution] = useState(initial.monthlyContribution);
	const [annualInterestRate, setAnnualInterestRate] = useState(initial.annualInterestRate);
	const [box3Enabled, setBox3Enabled] = useState(false);
	const [isMobile, setIsMobile] = useState(() => window.innerWidth < 600);
	const canvasRef = useRef(null);
	const chartInstance = useRef(null);

	// Keep URL in sync so users can share their exact scenario
	useEffect(() => {
		const params = new URLSearchParams({
			jaren: years,
			start: principal,
			inleg: monthlyContribution,
			rendement: annualInterestRate,
		});
		window.history.replaceState(null, '', `/?${params.toString()}`);
	}, [years, principal, monthlyContribution, annualInterestRate]);

	const options = useMemo(
		() => ({
			years: parseInt(years) || 0,
			principal: parseInt(principal) || 0,
			monthlyContribution: parseInt(monthlyContribution) || 0,
			interestRate: (parseInt(annualInterestRate) || 0) / 100,
			box3Enabled,
		}),
		[years, principal, monthlyContribution, annualInterestRate, box3Enabled]
	);

	const { compounds, invested, box3Values } = useMemo(() => computeYearlyData(options), [options]);

	const totalValue = compounds[compounds.length - 1] ?? 0;
	const totalInvested = invested[invested.length - 1] ?? 0;
	const totalProfit = totalValue - totalInvested;
	const monthlyIncome = Math.floor(totalValue / 25 / 12);
	const box3Total = box3Values[box3Values.length - 1] ?? 0;
	const box3MonthlyIncome = Math.floor(box3Total / 25 / 12);

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < 600);
			chartInstance.current?.resize();
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		if (!canvasRef.current) return;
		const ctx = canvasRef.current.getContext('2d');

		const totalGradient = ctx.createLinearGradient(0, 0, 0, 500);
		totalGradient.addColorStop(0, 'rgba(79, 70, 229, 0.55)');
		totalGradient.addColorStop(1, 'rgba(79, 70, 229, 0.02)');

		const investedGradient = ctx.createLinearGradient(0, 0, 0, 500);
		investedGradient.addColorStop(0, 'rgba(34, 197, 94, 0.4)');
		investedGradient.addColorStop(1, 'rgba(34, 197, 94, 0.02)');

		const box3Gradient = ctx.createLinearGradient(0, 0, 0, 500);
		box3Gradient.addColorStop(0, 'rgba(245, 158, 11, 0.4)');
		box3Gradient.addColorStop(1, 'rgba(245, 158, 11, 0.02)');

		if (chartInstance.current) chartInstance.current.destroy();

		const datasets = [
			{
				label: t('chartTotalWealth'),
				data: compounds,
				fill: true,
				backgroundColor: totalGradient,
				borderColor: 'rgba(79, 70, 229, 1)',
				borderWidth: 2.5,
				tension: 0.4,
				pointRadius: 0,
				pointHoverRadius: 6,
				pointHoverBackgroundColor: 'rgba(79, 70, 229, 1)',
			},
			{
				label: t('chartInvested'),
				data: invested,
				fill: true,
				backgroundColor: investedGradient,
				borderColor: 'rgba(34, 197, 94, 1)',
				borderWidth: 2.5,
				tension: 0.4,
				pointRadius: 0,
				pointHoverRadius: 6,
				pointHoverBackgroundColor: 'rgba(34, 197, 94, 1)',
			},
		];

		if (box3Enabled) {
			datasets.splice(1, 0, {
				label: t('chartAfterBox3'),
				data: box3Values,
				fill: true,
				backgroundColor: box3Gradient,
				borderColor: 'rgba(245, 158, 11, 1)',
				borderWidth: 2.5,
				borderDash: [6, 3],
				tension: 0.4,
				pointRadius: 0,
				pointHoverRadius: 6,
				pointHoverBackgroundColor: 'rgba(245, 158, 11, 1)',
			});
		}

		chartInstance.current = new Chart(ctx, {
			type: 'line',
			data: {
				labels: getYearLabels(options.years),
				datasets,
			},
			options: {
				responsive: true,
				aspectRatio: isMobile ? 1.1 : 2.2,
				interaction: { mode: 'index', intersect: false },
				plugins: {
					legend: {
						position: 'top',
						labels: {
							usePointStyle: true,
							pointStyleWidth: 10,
							font: { family: 'Inter, sans-serif', size: isMobile ? 11 : 13 },
							padding: isMobile ? 10 : 20,
						},
					},
					tooltip: {
						backgroundColor: 'rgba(15, 15, 30, 0.85)',
						padding: 12,
						cornerRadius: 10,
						callbacks: {
							label: (ctx) => ` ${ctx.dataset.label}: ${formatEuro(ctx.raw)}`,
						},
					},
				},
				scales: {
					x: {
						grid: { display: false },
						ticks: {
							maxTicksLimit: isMobile ? 5 : 10,
							font: { family: 'Inter, sans-serif', size: isMobile ? 10 : 12 },
						},
					},
					y: {
						grid: { color: 'rgba(0,0,0,0.04)' },
						ticks: {
							callback: (value) => {
								if (isMobile) {
									if (value >= 1_000_000) return `€${(value / 1_000_000).toFixed(1)}M`;
									if (value >= 1_000) return `€${Math.round(value / 1_000)}k`;
									return `€${value}`;
								}
								return formatEuro(value);
							},
							font: { family: 'Inter, sans-serif', size: isMobile ? 10 : 12 },
						},
					},
				},
			},
		});

		return () => chartInstance.current?.destroy();
	}, [compounds, invested, box3Values, box3Enabled, options.years, isMobile, language]); // eslint-disable-line react-hooks/exhaustive-deps

	const homeJsonLd = {
		'@context': 'https://schema.org',
		'@type': 'WebApplication',
		name: 'Visualiseer mijn pensioen',
		url: 'https://www.visualiseermijnpensioen.nl/',
		description: language === 'en'
			? 'Free online pension planner: calculate how early you can retire based on your monthly contribution and compound interest.'
			: 'Gratis online pensioenplanner: bereken hoe vroeg je met pensioen kunt gaan op basis van maandelijkse inleg en samengestelde rente.',
		applicationCategory: 'FinanceApplication',
		operatingSystem: 'Any',
		inLanguage: language === 'en' ? 'en' : 'nl-NL',
		offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
	};

	const faqJsonLd = language === 'en' ? {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: [
			{
				'@type': 'Question',
				name: 'How does the pension planner work?',
				acceptedAnswer: { '@type': 'Answer', text: 'Enter your monthly contribution, starting capital, investment duration, and expected annual return. The planner calculates your wealth year by year using compound interest, including the impact of Dutch Box 3 tax from 2028.' },
			},
			{
				'@type': 'Question',
				name: 'What is the 4% rule?',
				acceptedAnswer: { '@type': 'Answer', text: 'The 4% rule states that you can safely withdraw 4% of your invested wealth per year without ever running out of money. With €1,000,000 in wealth you can withdraw €40,000 per year (€3,333/month). For Dutch investors, Box 3 tax reduces this to roughly 2.5–3% effectively.' },
			},
			{
				'@type': 'Question',
				name: 'What is FIRE?',
				acceptedAnswer: { '@type': 'Answer', text: 'FIRE stands for Financial Independence, Retire Early. The goal is to save and invest aggressively until your wealth generates enough passive income to cover your living expenses — making work optional. Your FIRE number is 25x your annual expenses.' },
			},
			{
				'@type': 'Question',
				name: 'How much do I need to invest monthly to retire early?',
				acceptedAnswer: { '@type': 'Answer', text: 'That depends on your target wealth, time horizon, and expected return. With €500/month at 7% return over 30 years you accumulate approximately €567,000. Use the planner to enter your own numbers and see the exact projection.' },
			},
			{
				'@type': 'Question',
				name: 'What is Box 3 tax?',
				acceptedAnswer: { '@type': 'Answer', text: 'Box 3 is a Dutch wealth tax. From 2028, it taxes your actual investment returns (dividends, interest, capital gains) at 36% above a €1,800 annual exemption. Toggle the Box 3 switch in the planner to see the impact on your final wealth.' },
			},
		],
	} : {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: [
			{
				'@type': 'Question',
				name: 'Hoe werkt de pensioenplanner?',
				acceptedAnswer: { '@type': 'Answer', text: 'Voer je maandelijkse inleg, startkapitaal, beleggingsduur en verwacht jaarrendement in. De planner berekent je vermogen jaar voor jaar via samengestelde rente, inclusief de impact van Box 3 belasting vanaf 2028.' },
			},
			{
				'@type': 'Question',
				name: 'Wat is de 4% regel?',
				acceptedAnswer: { '@type': 'Answer', text: 'De 4% regel stelt dat je jaarlijks 4% van je belegd vermogen kunt opnemen zonder dat het ooit opraakt. Met €1.000.000 vermogen kun je €40.000 per jaar (€3.333/mnd) opnemen. Voor Nederlandse beleggers verlaagt Box 3 dit effectief naar ongeveer 2,5–3%.' },
			},
			{
				'@type': 'Question',
				name: 'Wat is FIRE?',
				acceptedAnswer: { '@type': 'Answer', text: 'FIRE staat voor Financial Independence, Retire Early. Het doel is agressief sparen en beleggen totdat je vermogen genoeg passief inkomen genereert om van te leven — zodat werk optioneel wordt. Je FIRE-getal is 25x je jaaruitgaven.' },
			},
			{
				'@type': 'Question',
				name: 'Hoeveel moet ik maandelijks inleggen om eerder met pensioen te gaan?',
				acceptedAnswer: { '@type': 'Answer', text: 'Dat hangt af van je doelvermogen, tijdshorizon en verwacht rendement. Met €500/mnd bij 7% rendement gedurende 30 jaar bouw je ongeveer €567.000 op. Gebruik de planner om je eigen cijfers in te voeren en de exacte projectie te zien.' },
			},
			{
				'@type': 'Question',
				name: 'Wat is Box 3 belasting?',
				acceptedAnswer: { '@type': 'Answer', text: 'Box 3 is de Nederlandse vermogensrendementsheffing. Vanaf 2028 belast het je werkelijke beleggingsrendement (dividend, rente, vermogenswinst) met 36% boven een vrijstelling van €1.800 per jaar. Zet de Box 3 schakelaar aan om de impact op je eindvermogen te zien.' },
			},
		],
	};

	return (
		<Stack spacing={3} pb={3}>
			<SEOHead
				title={language === 'en'
					? 'Retire early — calculate your pension | VisualiseermijnPensioen.nl'
					: 'Sneller met pensioen — bereken je vroegpensioen | VisualiseermijnPensioen.nl'}
				description={language === 'en'
					? 'Enter your monthly contribution and see the power of compound interest grow year by year. Free pension planner with Box 3 tax impact.'
					: 'Voer je maandelijkse inleg in en zie de kracht van samengestelde rente jaar voor jaar groeien. Gratis pensioenplanner inclusief Box 3-impact.'}
				canonical="https://www.visualiseermijnpensioen.nl/"
				lang={language === 'en' ? 'en' : 'nl'}
				jsonLd={[homeJsonLd, faqJsonLd]}
			/>
			<Grid container spacing={2}>
				<Grid size={{ xs: 6, md: 3 }}>
					<TextField
						fullWidth
						label={t('labelDuration')}
						variant="outlined"
						type="number"
						value={years}
						onChange={(e) => setYears(e.target.value)}
						slotProps={{
							input: {
								endAdornment: <InputAdornment position="end">{t('labelYears')}</InputAdornment>,
							},
						}}
					/>
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<TextField
						fullWidth
						label={t('labelInitialDeposit')}
						variant="outlined"
						type="number"
						value={principal}
						onChange={(e) => setPrincipal(e.target.value)}
						slotProps={{
							input: {
								startAdornment: <InputAdornment position="start">€</InputAdornment>,
							},
						}}
					/>
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<TextField
						fullWidth
						label={t('labelMonthlyDeposit')}
						variant="outlined"
						type="number"
						value={monthlyContribution}
						onChange={(e) => setMonthlyContribution(e.target.value)}
						slotProps={{
							input: {
								startAdornment: <InputAdornment position="start">€</InputAdornment>,
							},
						}}
					/>
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<TextField
						fullWidth
						label={t('labelReturn')}
						variant="outlined"
						type="number"
						value={annualInterestRate}
						onChange={(e) => setAnnualInterestRate(e.target.value)}
						slotProps={{
							input: {
								endAdornment: <InputAdornment position="end">%</InputAdornment>,
							},
						}}
					/>
				</Grid>
			</Grid>

			<Grid container spacing={2}>
				<Grid size={{ xs: 6, md: 3 }}>
					<StatCard
						label={t('labelTotalWealth')}
						value={formatEuro(totalValue)}
						highlight
						icon={TrendingUp}
						subValue={box3Enabled ? formatEuro(box3Total) : null}
						subLabel={t('labelAfterBox3')}
						delay={0}
					/>
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<StatCard label={t('labelTotalInvested')} value={formatEuro(totalInvested)} icon={Savings} delay={90} />
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<StatCard label={t('labelProfit')} value={formatEuro(totalProfit)} icon={ShowChart} delay={180} />
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<StatCard
						label={t('labelMonthlyIncome')}
						value={`${formatEuro(monthlyIncome)}${t('labelPerMonth')}`}
						icon={CalendarMonth}
						subValue={box3Enabled ? `${formatEuro(box3MonthlyIncome)}${t('labelPerMonth')}` : null}
						subLabel={t('labelAfterBox3')}
						delay={270}
					/>
				</Grid>
			</Grid>

			<Box sx={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
				<FormControlLabel
					control={
						<Switch
							checked={box3Enabled}
							onChange={(e) => setBox3Enabled(e.target.checked)}
							color="warning"
						/>
					}
					label={
						<Typography variant="body2" fontWeight={500}>
							{t('box3Label')}
						</Typography>
					}
				/>
				{box3Enabled && (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
						<Box sx={{ bgcolor: '#fff3e0', border: '1px solid #ffb74d', borderRadius: 2, px: 2, py: 1 }}>
							<Typography variant="caption" sx={{ color: '#e65100' }}>
								{t('box3Warning')}
							</Typography>
						</Box>
						<Box
							onClick={() => navigate('/emigreren')}
							sx={{
								display: 'inline-flex',
								alignItems: 'center',
								gap: 0.75,
								border: '1px solid #ffb74d',
								color: '#e65100',
								fontWeight: 600,
								borderRadius: 2,
								px: 1.5,
								py: 0.75,
								fontSize: 13,
								cursor: 'pointer',
								userSelect: 'none',
								'&:hover': { bgcolor: '#fff3e0', borderColor: '#e65100' },
							}}
						>
							<FlightTakeoff sx={{ fontSize: 16 }} />
							{t('box3CTA')}
						</Box>
					</Box>
				)}
			</Box>

			<Box
				className="animate-fade-in"
				style={{ '--delay': '0.35s' }}
				sx={{ borderRadius: 3, p: 2, bgcolor: 'grey.50' }}
			>
				<canvas ref={canvasRef} />
			</Box>

			<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
				<Tooltip title={copied ? t('tooltipCopied') : t('tooltipShare')} placement="top">
					<IconButton
						size="small"
						onClick={() => {
							navigator.clipboard.writeText(window.location.href);
							setCopied(true);
							setTimeout(() => setCopied(false), 2000);
						}}
						sx={{ color: copied ? 'success.main' : 'text.secondary' }}
					>
						<Share fontSize="small" />
					</IconButton>
				</Tooltip>
			</Box>

			<Stack spacing={0.5}>
				<Typography variant="caption" color="text.secondary">
					{t('footerNote1')}
				</Typography>
				{box3Enabled && (
					<Typography variant="caption" color="text.secondary">
						{t('footerNote2')}
					</Typography>
				)}
			</Stack>
		</Stack>
	);
};

export default Graph;
